import { useSettlementToken } from "features/tokens/useSettlementToken";
import React from "react";
import Button from "components/button/Button";
import "./amount-form.css";

interface Props {
  amount: string;
  onChange: (v: string) => void;
  onNext: () => void;
  onCancel: () => void;
}

/**
 * Keep digits and a single decimal point. The input is `type="text"` on purpose:
 * `type="number"` accepts exponent/sign characters (`e`, `+`, `-`) and reports an
 * empty value while leaving them visible, so they can't be filtered out here.
 */
function sanitizeAmount(raw: string): string {
  const cleaned = raw.replace(/[^\d.]/g, "");
  const [whole, ...rest] = cleaned.split(".");
  return rest.length ? `${whole}.${rest.join("")}` : whole;
}

function AmountForm({ amount, onChange, onNext, onCancel }: Props) {
  const { label: tokenSymbol } = useSettlementToken();
  const parsed = parseFloat(amount);
  const isValid = amount !== "" && !isNaN(parsed) && parsed > 0;

  const handleKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && isValid) onNext();
  };

  return (
    <div className="tu-amount-form">
      <div className="tu-amount-header">
        <h3 className="v1-modal-title">Top Up Card</h3>
        <p className="v1-modal-subtitle">
          Enter the {tokenSymbol} amount to top up.
        </p>
      </div>

      <div className="tu-field">
        <label className="tu-label" htmlFor="tu-amount">
          Amount
        </label>
        <div className="tu-amount-input-wrap">
          <input
            id="tu-amount"
            className="tu-input"
            type="text"
            inputMode="decimal"
            placeholder="0.00"
            value={amount}
            onChange={(e) => onChange(sanitizeAmount(e.target.value))}
            onKeyDown={handleKey}
            autoComplete="off"
            autoFocus
          />
          <span className="tu-amount-unit">{tokenSymbol}</span>
        </div>
      </div>

      <div className="v1-modal-actions">
        <Button variant="ghost" size="md" onClick={onCancel}>
          Cancel
        </Button>
        <Button variant="primary" size="md" onClick={onNext} disabled={!isValid}>
          Next
        </Button>
      </div>
    </div>
  );
}

export default AmountForm;
